import { API_BASE_URL } from '../api/config';
import { FILES_ENDPOINTS, PDF_ENDPOINTS } from '../endpoints';
import { BaseEntityType } from '../types';
// entityCRDTService removed — entity-system Y.Doc handles sync via REST broadcast
import { entityService } from './entityService';
import fetchService from './fetchService';
import { v4 as uuidv4 } from 'uuid';

import { createFileLogger } from '../utils/logger';

const log = createFileLogger('FILE_SERVICE', { enabled: false });

// Simple file data cache to avoid re-downloading the same files
interface CachedFileData {
  data: Uint8Array;
  timestamp: number;
  size: number;
}

class FileDataCache {
  private cache = new Map<string, CachedFileData>();
  private readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
  private readonly MAX_CACHE_SIZE = 50 * 1024 * 1024; // 50MB max cache
  
  get(entityId: string): Uint8Array | null {
    const cached = this.cache.get(entityId);
    if (!cached) return null;
    
    // Check if cache is still fresh
    const isRecentEnough = (Date.now() - cached.timestamp) < this.CACHE_DURATION;
    if (!isRecentEnough) {
      this.cache.delete(entityId);
      return null;
    }
    
    log.log(`[FileDataCache] Cache hit for ${entityId}, size: ${cached.size} bytes`);
    return cached.data;
  }
  
  set(entityId: string, data: Uint8Array): void {
    // Check cache size and clean if needed
    this.cleanupIfNeeded();
    
    this.cache.set(entityId, {
      data,
      timestamp: Date.now(),
      size: data.length
    });
    
    log.log(`[FileDataCache] Cached file data for ${entityId}, size: ${data.length} bytes`);
  }
  
  private cleanupIfNeeded(): void {
    const totalSize = Array.from(this.cache.values()).reduce((sum, item) => sum + item.size, 0);
    
    if (totalSize > this.MAX_CACHE_SIZE) {
      // Remove oldest entries until under limit
      const entries = Array.from(this.cache.entries()).sort((a, b) => a[1].timestamp - b[1].timestamp);
      
      let currentSize = totalSize;
      for (const [entityId, data] of entries) {
        if (currentSize <= this.MAX_CACHE_SIZE * 0.8) break; // Clean to 80% of limit
        
        this.cache.delete(entityId);
        currentSize -= data.size;
        log.log(`[FileDataCache] Evicted ${entityId} from cache, size: ${data.size} bytes`);
      }
    }
  }
  
  clear(): void {
    this.cache.clear();
    log.log('[FileDataCache] Cache cleared');
  }
}

const fileDataCache = new FileDataCache();

export interface QueueInfo {
  queuePosition?: number;
  activeProcessing?: number;
  queuedTotal?: number;
  estimatedWaitSeconds?: number;
}

export interface EnrichmentStatus {
  status: 'not_started' | 'queued' | 'processing' | 'completed' | 'completed_partial' | 'completed_no_search' | 'failed';
  internal_status?: string; // For debugging - shows actual backend status
  error?: string | null;
  warning?: string | null; // For partial completion warnings
  queueInfo?: QueueInfo | null; // Queue information for queued status
  data?: {
    summary?: string;
    totalCharacters?: number;
    totalPages?: number;
    totalChunks?: number;
    searchIndexed?: boolean | string;
    completedAt?: string;
  };
}

export interface PdfEnrichmentResult {
  raw_text: string;
  sections: Record<string, string>;
  chunks: any[];
  metadata: any;
  summaries: Record<string, string>;
}

export class FileService {
  async getList(parentEntityId?: string): Promise<BaseEntityType[]> {
    try {
      // Use the projects endpoint for entities when we're in project context
      // Otherwise fallback to the legacy files endpoint
      const endpoint = FILES_ENDPOINTS.list(parentEntityId);

      const response = await fetchService(endpoint);

      // Filter out invalid entities before mapping
      return response
        .filter((entity: any) => {
          if (!entity || !entity._id) {
            log.warn('[FileService] Skipping invalid entity:', entity);
            return false;
          }
          return true;
        })
        .map((entity: any) => {
          // Determine entity type from various possible sources
          const entityType = entity.entityType || entity.type || 'File';

          // Preserve windows for Space entities
          if (entityType === 'Space') {
            return {
              _id: entity._id,
              id: entity._id,
              name: entity.name || 'Untitled!',
              type: entityType.toLowerCase(),
              entityType: entityType,
              created_at: entity.created_at || new Date().toISOString(),
              updated_at: entity.updated_at || new Date().toISOString(),
              skeleton: {
                '@type': entityType,
                ...entity.skeleton, // Keep existing skeleton including windows
              },
            };
          }

          // For File entities, use skeleton.fileName as the primary name source
          const displayName =
            entityType === 'File' ? entity.skeleton?.fileName || entity.name || 'Untitled' : entity.name || 'Untitled';

          // Regular mapping for other entities...
          return {
            _id: entity._id,
            id: entity._id,
            name: displayName,
            type: entityType.toLowerCase(),
            entityType: entityType,
            created_at: entity.created_at || new Date().toISOString(),
            updated_at: entity.updated_at || new Date().toISOString(),
            skeleton: {
              '@type': entityType,
              ...(entity.skeleton || {}),
              // Ensure minimal skeleton properties based on type
              ...(entityType === 'File'
                ? {
                    fileId: entity._id,
                    fileName: entity.skeleton?.fileName || entity.name || 'Untitled',
                    groupIds: [],
                    mimeType: entity.mimeType || 'application/octet-stream',
                    size: entity.size || 0,
                  }
                : {}),
              ...(entityType === 'Group'
                ? {
                    childEntities: entity.skeleton?.childEntities || [],
                  }
                : {}),
            },
          };
        });
    } catch (error) {
      log.error('[FileService] Failed to get list:', error);
      throw error;
    }
  }

  async addFile(file: File, parentEntityId?: string, spaceId?: string): Promise<BaseEntityType> {
    log.log('[FileService] Adding file:', { name: file.name, parentId: parentEntityId, spaceId });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('entityType', 'File');
    
    // Add spaceId if provided (space-agnostic uploads don't include spaceId)
    if (spaceId) {
      formData.append('spaceId', spaceId);
      log.log('[FileService] Adding spaceId to form data:', spaceId);
    } else {
      log.log('[FileService] No spaceId provided - space-agnostic upload');
    }
    
    // ✅ CRITICAL FIX: Pass parentId to backend so edge is created with correct parent
    // Without this, backend creates edge with spaceId as parent (ROOT)
    if (parentEntityId) {
      formData.append('parentId', parentEntityId);
      log.log('[FileService] Adding parentId to form data:', parentEntityId);
    }
    
    // Debug CSV file upload request
    if (file.name?.includes('.csv')) {
      console.log('[FileService CSV DEBUG] Uploading file:', {
        fileName: file.name,
        fileType: file.type,
        entityTypeBeingSent: 'File'
      });
    }

    // Determine the correct endpoint based on whether we're in a project context
    const endpoint = FILES_ENDPOINTS.add();

    const response = await fetchService(endpoint, {
      method: 'POST',
      body: formData,
    });

    // Debug CSV file handling specifically
    if (file.name?.includes('.csv')) {
      console.log('[FileService CSV DEBUG] Backend response:', {
        backendEntityType: response.entityType,
        fileName: file.name,
        responseKeys: Object.keys(response)
      });
    }
    
    // Generate ID if backend doesn't provide one
    const entityId = response._id || response.id || uuidv4();
    
    // ✅ CRITICAL: Set groupIds if parentEntityId provided
    // This prevents files from appearing at root when dropped into folders
    const groupIds = parentEntityId 
      ? [parentEntityId]  // Use parentId as groupId
      : (response.skeleton?.groupIds || []);  // Fallback to backend groupIds
    
    // ✅ CRITICAL: Remove isDuplicate flag from skeleton
    // When users explicitly upload files (via QuickLauncher, EntitySidebar, etc.),
    // they want to see them even if backend detected them as duplicates by contentHash.
    // The backend uses isDuplicate for deduplication optimization, but frontend should
    // always show explicitly uploaded entities.
    const cleanSkeleton = { ...response.skeleton };
    if (cleanSkeleton.isDuplicate !== undefined) {
      log.log('[FileService] Removing isDuplicate flag from skeleton:', {
        fileName: file.name,
        wasDuplicate: cleanSkeleton.isDuplicate,
        originalEntityId: cleanSkeleton.originalEntityId
      });
      delete cleanSkeleton.isDuplicate;
      delete cleanSkeleton.originalEntityId; // Also remove the reference to original
      delete cleanSkeleton.deduplicationStrategy; // And the strategy
    }
    
    const finalSpaceId = spaceId || response.spaceId;
    
    // ✅ CRITICAL: Log if spaceId is missing (would cause entity to disappear after refresh)
    if (!finalSpaceId) {
      log.warn('[FileService] ⚠️ File entity created WITHOUT spaceId - entity may disappear after refresh!', {
        fileName: file.name,
        entityId,
        parameterSpaceId: spaceId,
        responseSpaceId: response.spaceId
      });
    }
    
    const entity: BaseEntityType = {
      ...response,
      _id: entityId,
      id: entityId,
      name: response.name || file.name,
      // Use backend's hierarchical entityType instead of hardcoding
      type: response.entityType || response.type || 'File',
      entityType: response.entityType || 'File',
      created_at: response.created_at || new Date().toISOString(),
      updated_at: response.updated_at || new Date().toISOString(),
      // Merge backend skeleton with frontend fields (preserve backend fields like path, thumbnailPath, icon)
      skeleton: {
        ...cleanSkeleton, // Use cleaned skeleton without isDuplicate
        '@type': 'File',
        fileId: entityId,
        fileName: file.name,
        groupIds,  // Use computed groupIds (includes parentEntityId if provided)
        mimeType: file.type,
        size: file.size,
        parentId: parentEntityId, // Store the parent ID in the skeleton for persistence
      },
      spaceId: finalSpaceId,  // ✅ CRITICAL FIX: Don't overwrite backend's spaceId with undefined
    };
    
    // Log if we had to generate an ID (indicates backend issue)
    if (!response._id && !response.id) {
      log.warn(`[FileService] Backend did not provide entity ID for file ${file.name}, generated: ${entityId}`);
    }
    
    // Debug CSV entity creation result
    if (file.name?.includes('.csv')) {
      console.log('[FileService CSV DEBUG] Final entity created:', {
        entityType: entity.entityType,
        type: entity.type,
        fileName: file.name,
        skeleton: entity.skeleton
      });
    }

    // ✅ ARCHITECTURE: Use entityStore mutation funnel for proper state management
    // Backend already created the entity, we're just syncing to frontend state
    const { default: useEntityStore } = await import('../stores/entityStore');
    const entityStore = useEntityStore.getState();
    
    // Use the single mutation funnel (handles CRDT sync, undo/redo, state update)
    const finalEntity = entityStore._applyLocalUpsert(entity, {
      source: 'local',
      log: true,
      syncCRDT: !!spaceId
    });
    
    // ✅ ARCHITECTURE: Emit entity created event (source is hardcoded to 'local' in entityCreated)
    // This notifies external listeners (EntitySidebar, etc.) about the new entity
    // EntityStore will ignore it because source='local' means already processed
    const { entityEventService } = await import('./entityEventService');
    entityEventService.entityCreated(finalEntity, spaceId || '', {
      command: 'file-upload'
    });
    
    // ✅ REMOVED: No longer needed - parentId is now sent to backend during upload
    // Backend sets skeleton.groupIds BEFORE creating entity, so edge is created correctly
    // Previous code updated groupIds AFTER entity creation, causing edge to have wrong parent
    
    // ✅ CRITICAL FIX: Create edge in dataspace tree so file appears in sidebar
    // This is required for uploaded files to appear correctly without page reload
    if (spaceId) {
      try {
        const { useDataspaceTreeStore } = await import('../stores/dataspaceTreeStore');
        const treeStore = useDataspaceTreeStore.getState();
        
        // ✅ GUARD: Check if tree is initialized before creating edge
        // If tree not ready, migration will create edge on next load
        if (!treeStore.initialized || !treeStore.dataspaceId) {
          log.log(`[FileService] Tree not initialized yet, skipping edge creation. Migration will handle it.`, {
            fileId: finalEntity._id.slice(-8),
            initialized: treeStore.initialized,
            hasDataspaceId: !!treeStore.dataspaceId
          });
        } else {
          // Ensure node exists and is positioned correctly
          treeStore.ensureNodeExists(finalEntity._id, parentEntityId || spaceId);
          
          log.log(`[FileService] ✅ Created edge in dataspace tree:`, { 
            fileId: finalEntity._id, 
            parentId: parentEntityId || spaceId 
          });
        }
      } catch (error) {
        log.error(`[FileService] Failed to create edge in tree:`, error);
      }
    }

    // If this is a PDF file, start enrichment process in background
    if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
      const pdfTraceId = `PDF_${finalEntity._id}_${Date.now()}`;
      try {
        console.log(`[${pdfTraceId}] 📄 PDF DETECTED - Starting enrichment pipeline`);
        console.log(`[${pdfTraceId}] Entity ID: ${finalEntity._id}`);
        console.log(`[${pdfTraceId}] LLM ID: ${finalEntity.llmId}`);
        console.log(`[${pdfTraceId}] File name: ${file.name}`);
        log.log(`[${pdfTraceId}] Starting PDF enrichment in background`);
        
        // Start enrichment process asynchronously (don't await)
        this.startPdfEnrichmentBackground(finalEntity._id, false, pdfTraceId).catch(error => {
          console.error(`[${pdfTraceId}] ❌ Background PDF enrichment failed:`, error);
          log.error(`[${pdfTraceId}] Background PDF enrichment failed:`, error);
        });

        console.log(`[${pdfTraceId}] ✅ Enrichment triggered, entity returned immediately`);
      } catch (error) {
        console.error(`[${pdfTraceId}] ❌ Error starting PDF enrichment:`, error);
        log.error(`[${pdfTraceId}] Error starting PDF enrichment:`, error);
      }
    }

    return finalEntity;
  }

  async startPdfEnrichmentBackground(fileId: string, fastMode = false, traceId?: string): Promise<void> {
    const trace = traceId || `PDF_${fileId}`;
    
    try {
      console.log(`[${trace}] 🔄 Calling backend enrichment API...`);
      log.log(`[${trace}] Starting PDF enrichment for: ${fileId}`);
      
      // 1. Start enrichment (returns immediately with 202 status)
      await fetchService(PDF_ENDPOINTS.enrich(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Trace-Id': trace,  // Pass trace ID to backend
        },
        body: JSON.stringify({
          file_id: fileId,
          fast_mode: fastMode,
          trace_id: trace,
        }),
      });
      
      console.log(`[${trace}] ✅ Backend accepted enrichment request (202)`);
      log.log(`[${trace}] Enrichment started, now polling for completion...`);
      
      // 2. Poll status endpoint until complete (max 10 minutes for large queues)
      const startTime = Date.now();
      const timeout = 600_000; // 10 minutes (increased from 3 for large queues)
      
      while (true) {
        // Check timeout
        if (Date.now() - startTime > timeout) {
          log.error('[FileService] PDF enrichment timed out after 10 minutes');
          return; // Don't throw - just exit silently
        }
        
        // Check status
        const status = await this.getPdfEnrichmentStatus(fileId);
        console.log(`[${trace}] 📊 Enrichment status check:`, status);
        
        // Log queue info if available
        if (status.queueInfo) {
          console.log(`[${trace}] 📋 Queue info:`, {
            position: status.queueInfo.queuePosition,
            active: status.queueInfo.activeProcessing,
            queued: status.queueInfo.queuedTotal
          });
        }
        
        if (status.status === 'completed' || 
            status.status === 'completed_partial' || 
            status.status === 'completed_no_search') {
          console.log(`[${trace}] ✅ PDF ENRICHMENT COMPLETED`);
          console.log(`[${trace}] Status: ${status.status}`);
          log.log(`[${trace}] PDF enrichment completed:`, { fileId, status: status.status });
          
          // 3. Fetch updated entity with pdfAsDocument
          const updatedEntity = await entityService.get(fileId);
          
          console.log(`[${trace}] 📦 Fetched updated entity:`, {
            fileId,
            enrichmentStatus: updatedEntity.skeleton?.enrichmentStatus,
            hasPdfAsDocument: !!updatedEntity.skeleton?.pdfAsDocument,
            hasEnrichedData: !!updatedEntity.skeleton?.enrichedData,
            hasReferenceMetadata: !!updatedEntity.skeleton?.referenceMetadata
          });
          
          // 4. ✅ ARCHITECTURE: Use public store method (backward compatibility wrapper)
          // This calls _applyLocalUpsert internally with correct options
          const { default: useEntityStore } = await import('../stores/entityStore');
          const entityStore = useEntityStore.getState();
          entityStore.updateEntity(updatedEntity);
          
          console.log(`[${trace}] ✅ EntityStore updated with enriched PDF data`);
          
          // 5. Emit entity updated event (for other listeners like PDFiumApp)
          // Note: EntityStore already processed via updateEntity(), but other components need the event
          const { entityEventService } = await import('./entityEventService');
          entityEventService.entityUpdated(updatedEntity, updatedEntity.spaceId || '', {
            command: 'pdf-enrichment-complete'
          });
          
          console.log(`[${trace}] ✅ Event emitted - all caches/listeners notified`);
          return; // Success!
        }
        
        if (status.status === 'failed') {
          log.error('[FileService] PDF enrichment failed:', status.error);
          return; // Exit silently
        }
        
        // Adaptive polling interval based on status
        let pollInterval = 3_000; // Default: 3 seconds
        
        if (status.status === 'queued') {
          // Poll less frequently when queued (every 5 seconds)
          pollInterval = 5_000;
          log.log('[FileService] PDF queued, waiting 5 seconds...');
        } else if (status.status === 'processing') {
          // Poll more frequently when actively processing (every 2 seconds)
          pollInterval = 2_000;
          log.log('[FileService] PDF processing, waiting 2 seconds...');
        } else {
          // Not started or unknown status - poll normally
          log.log('[FileService] PDF status:', status.status, '- waiting 3 seconds...');
        }
        
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
    } catch (error) {
      log.error('[FileService] PDF enrichment error:', error);
      // Don't throw - this shouldn't block file upload
    }
  }

  async enrichPdfAndWait(fileId: string, pollingInterval = 5_000, timeout = 180_000, fastMode = false): Promise<void> {
    // Start enrichment process
    await fetchService(PDF_ENDPOINTS.enrich(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        file_id: fileId,
        fast_mode: fastMode,
      }),
    });

    // Start polling with timeout
    const startTime = Date.now();

    while (true) {
      // Check if we've exceeded timeout
      if (Date.now() - startTime > timeout) {
        throw new Error('PDF enrichment timed out');
      }

      // Get current status
      const status = await this.getPdfEnrichmentStatus(fileId);

      if (
        status.status === 'completed' ||
        status.status === 'completed_partial' ||
        status.status === 'completed_no_search'
      ) {
        // Log completion details including warnings
        log.log('[FileService] PDF enrichment completed:', {
          fileId,
          status: status.status,
          internal_status: status.internal_status,
          warning: status.warning,
          searchIndexed: status.data?.searchIndexed,
          totalChunks: status.data?.totalChunks,
          totalPages: status.data?.totalPages,
        });

        // Handle different completion types
        if (status.status === 'completed_partial') {
          log.warn(
            '[FileService] PDF enrichment completed partially:',
            status.warning || 'Some enrichment steps failed',
          );
        } else if (status.status === 'completed_no_search') {
          log.warn(
            '[FileService] PDF enrichment completed without search indexing:',
            status.warning || 'Search indexing failed',
          );
        }

        // If there's a warning, log it but don't fail
        if (status.warning) {
          log.warn('[FileService] PDF enrichment completed with warning:', status.warning);
        }

        // Load the enriched data (should be available even for partial completion)
        await this.loadPdfEnrichment(fileId);
        break;
      } else if (status.status === 'failed') {
        throw new Error(status.error || 'PDF enrichment failed');
      } else if (status.error) {
        // Handle case where status is 'processing' but there's an error
        // This indicates a connection or infrastructure issue
        throw new Error(`PDF enrichment error: ${status.error}`);
      }

      // Wait before next poll
      await new Promise((resolve) => setTimeout(resolve, pollingInterval));
    }
  }

  async readFile(entityId: string, parentEntityId?: string): Promise<Uint8Array> {
    // Check cache first
    const cachedData = fileDataCache.get(entityId);
    if (cachedData) {
      return cachedData;
    }
    
    const maxRetries = 3;
    let lastError: any = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        log.log(`[FileService] Reading file attempt ${attempt + 1}/${maxRetries} for entity ID: ${entityId}`);
        // Use custom fetch options for file downloads to avoid CORS issues with credentials
        const data = await fetchService(FILES_ENDPOINTS.read(entityId, parentEntityId), {
          credentials: 'omit', // Don't send credentials for file downloads
          mode: 'cors',
        });

        if (data && data.length > 0) {
          log.log(`[FileService] Successfully read file, size: ${data.length} bytes`);
          
          // Cache the file data
          fileDataCache.set(entityId, data);
          
          return data;
        } else {
          log.warn(`[FileService] Received empty data for file: ${entityId}`);

          // If this isn't the last attempt, try again
          if (attempt < maxRetries - 1) {
            await new Promise((resolve) => setTimeout(resolve, 1000 * Math.pow(2, attempt)));
            continue;
          }

          throw new Error('Received empty data from server');
        }
      } catch (error: any) {
        log.error(`[FileService] Error reading file (attempt ${attempt + 1}/${maxRetries}):`, error);
        lastError = error;

        // Don't retry auth/permission failures — they won't resolve on retry.
        if (error?.status === 401 || error?.status === 403 || error?.status === 404) {
          break;
        }

        // Retry with exponential backoff for transient errors.
        if (attempt < maxRetries - 1) {
          const backoffMs = 1000 * Math.pow(2, attempt);
          log.log(`[FileService] Retrying in ${backoffMs}ms...`);
          await new Promise((resolve) => setTimeout(resolve, backoffMs));
        }
      }
    }

    // If we got here, all retries failed
    log.error('[FileService] All attempts to read file failed:', lastError);
    throw lastError || new Error('Failed to read file after multiple attempts');
  }

  async updateFile(file: File, entityId: string, parentEntityId?: string): Promise<Uint8Array> {
    const formData = new FormData();
    formData.append('file', file);

    return fetchService(FILES_ENDPOINTS.update(entityId, parentEntityId), {
      method: 'PUT',
      body: formData,
    });
  }

  async deleteFile(entityId: string, parentEntityId?: string): Promise<Uint8Array> {
    return fetchService(FILES_ENDPOINTS.update(entityId, parentEntityId), {
      method: 'DELETE',
    });
  }

  async getPdfEnrichmentStatus(fileId: string): Promise<EnrichmentStatus> {
    return fetchService(`${PDF_ENDPOINTS.enrichStatus()}?fileId=${fileId}`, {
      method: 'GET',
    });
  }

  async loadPdfEnrichment(fileId: string): Promise<PdfEnrichmentResult | null> {
    try {
      const response = await fetchService(`${API_BASE_URL}/files/inspect-pdf`, {
        method: 'POST',
        body: JSON.stringify({ fileId }),
      });
      return response.skeleton.enrichments;
    } catch (error) {
      // Gracefully handle case where PDF isn't enriched yet
      log.log('[FileService] PDF not enriched yet or enrichment failed:', fileId);
      return null;
    }
  }

  async uploadFile(spaceId: string, file: File): Promise<BaseEntityType> {
    log.log('[FileService] Uploading file:', {
      name: file.name,
      size: file.size,
      type: file.type,
      spaceId,
    });

    const id = uuidv4();
    // Create file entity with all required fields
    const fileEntity: BaseEntityType = {
      _id: id,
      id: id,
      name: file.name,
      type: 'File',
      entityType: 'File',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      skeleton: {
        '@type': 'File',
        path: file.name,
        fileName: file.name,
        size: file.size,
        type: file.type,
      },
    };

    // Real-time sync handled by entity-system Y.Doc (broadcast from REST handler).

    return fileEntity;
  }

  async getRecentFiles(type?: string): Promise<Array<{ id: string; name: string }>> {
    try {
      const response = await fetchService(`${API_BASE_URL}/files/recent${type ? `?type=${type}` : ''}`);
      return response;
    } catch (error) {
      log.error('Error fetching recent files:', error);
      return [];
    }
  }
}

// Create and export the singleton instance
export const fileService = new FileService();

// React hook to use the service
export const useFileService = () => fileService;
